import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";

const getQuestionTypeCounts = (questions = [], t) => {
  const counts = {};
  const key = "allTemplates.checkInTemplates.questionTypes.";
  for (const question of questions) {
    const label = t(`${key}${question.type}`) || question.type;
    counts[label] = (counts[label] || 0) + 1;
  }
  return Object.entries(counts);
};

const CheckInTemplateCard = ({ template, activeUserToRequest }) => {
  const { t } = useTranslation("coaching-portal");
  const { title, questions = [] } = template;

  const typeCounts = getQuestionTypeCounts(questions, t);

  const removeTemplate = (id) => {
    if (confirm(t("allTemplates.checkInTemplates.confirmDeleteTemplate"))) {
      submit("/actions/questionnaire/remove-template", {
        method: "POST",
        body: {
          templateId: id,
          userToRequest: activeUserToRequest._id,
        },
      });
    }
  };

  const duplicateTemplate = (id) => {
    submit("/actions/questionnaire/duplicate-template", {
      method: "POST",
      body: {
        templateId: id,
        userToRequest: activeUserToRequest._id,
      },
    });
  };

  return (
    <div className="check-in-template-card">
      <div className="check-in-template-card__header">
        <h3 className="check-in-template-card__title">{title}</h3>
        <span className="check-in-template-card__count">
          {questions.length}{" "}
          {questions.length === 1
            ? t("allTemplates.checkInTemplates.question")
            : t("allTemplates.checkInTemplates.questions")}
        </span>
      </div>

      <div className="check-in-template-card__questions">
        <div className="check-in-template-card__questions-scroll">
          {typeCounts.map(([label, count]) => (
            <div key={label} className="check-in-template-card__question-row">
              <p className="p1">{count}</p>
              <span className="check-in-template-card__question-label">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="check-in-template-card__footer">
        <button
          type="button"
          className="check-in-template-card__edit-btn"
          data-template-id={template._id}
          onClick={() => {
            submit(`/actions/refresh/?`, {
              method: "GET",
              body: {
                "modal[type]": "add-questionnaire-template",
                templateId: template._id,
                section: "questionnaire",
                tabView: "check-in-tab",
                templateView: "check-in",
                fromEdit: true,
              },
            });
          }}
        >
          <img
            src="/img/new-icons/edit-pencil.svg"
            alt={t("allTemplates.checkInTemplates.editAlt")}
          />
          {t("allTemplates.checkInTemplates.editQuestionnaire")}
        </button>
        <div className="check-in-template-card__actions">
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={() => {
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  "modal[type]": "questionnaire-templates",
                  templateId: template._id,
                  tabView: "check-in-tab",
                  templateView: "check-in",
                },
              });
            }}
          >
            <SvgIcon
              src="/img/new-icons/eye.svg"
              alt={t("allTemplates.checkInTemplates.viewAlt")}
            />
          </button>
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={() => duplicateTemplate(template._id)}
          >
            <SvgIcon
              src="/img/new-icons/vuesax-linear-document-copy.svg"
              alt={t("allTemplates.checkInTemplates.duplicateAlt")}
            />
          </button>
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={() => removeTemplate(template._id)}
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              alt={t("allTemplates.checkInTemplates.deleteAlt")}
              noFilterOnLight={true}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

CheckInTemplateCard.propTypes = {
  template: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    questions: PropTypes.array,
  }).isRequired,
};

const sortByTitle = (a, b) =>
  (a.title || "").localeCompare(b.title || "", undefined, {
    sensitivity: "base",
  });
const sortByTitleDesc = (a, b) =>
  (b.title || "").localeCompare(a.title || "", undefined, {
    sensitivity: "base",
  });

const SORT_FUNCTIONS = {
  name: sortByTitle,
  nameDescending: sortByTitleDesc,
};

const CheckInTemplates = ({ templates = [], locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { activeUser, activeUserToRequest, query = {} } = locals || {};
  const referAsClients = Boolean(activeUser?.referAsClients);
  const sort = query.sort || "name";
  const sortFn = SORT_FUNCTIONS[sort] || sortByTitle;
  const sortedTemplates = [...templates].sort(sortFn);

  if (!templates.length) {
    return (
      <div className="no-programs-section">
        <p>{t("allTemplates.noTemplatesAdded")}</p>
        <span>
          {t("allTemplates.checkInTemplates.noTemplatesMessage", {
            role: referAsClients
              ? t("athletes.roleClients")
              : t("athletes.roleAthletes"),
          })}
        </span>
        <a
          role="button"
          data-button-type="primary"
          href="/actions/refresh/?modal[type]=questionnaire-templates&tabView=check-in-tab&templateView=check-in"
        >
          {t("allTemplates.addTemplate")}
        </a>
      </div>
    );
  }

  return (
    <section className="generic-template-section">
      <div className="generic-template-grid">
        {sortedTemplates.map((template) => (
          <CheckInTemplateCard
            key={template._id}
            template={template}
            activeUserToRequest={activeUserToRequest}
          />
        ))}
      </div>
    </section>
  );
};

CheckInTemplates.propTypes = {
  templates: PropTypes.array,
  locals: PropTypes.object,
};

export default CheckInTemplates;
